import { useState } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ProposedChange } from "./api"

interface RenameMergeDialogProps {
  /** Category name being renamed; null closes the dialog. */
  from_name: string | null
  category_names: string[]
  onClose: () => void
  onPreview: (change: ProposedChange, title: string) => void
}

/**
 * Rename a category, or merge it into another by picking an existing name. The two are
 * the same operation server-side; the copy switches when the target name already exists.
 */
export function RenameMergeDialog({ from_name, category_names, onClose, onPreview }: RenameMergeDialogProps) {
  const [to_name, setToName] = useState("")
  const merging = category_names.some((n) => n.toLowerCase() === to_name.trim().toLowerCase() && n.toLowerCase() !== (from_name ?? "").toLowerCase())

  function submit(e: Event) {
    e.preventDefault()
    if (!from_name || !to_name.trim()) return
    const change: ProposedChange = { kind: "rename_or_merge", from_name, to_name: to_name.trim() }
    onPreview(change, merging ? `Merge "${from_name}" into "${to_name.trim()}"` : `Rename "${from_name}" to "${to_name.trim()}"`)
    setToName("")
  }

  return (
    <Dialog open={from_name !== null} onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{merging ? `Merge "${from_name}" into "${to_name.trim()}"` : `Rename "${from_name}"`}</DialogTitle>
          <DialogDescription>
            All rules and deductions under “{from_name}” move to the new name. Pick an existing category to merge into it — you'll see exactly
            which deductions are affected before anything changes.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit as any} class="space-y-4">
          <div>
            <Label>New name</Label>
            <Input
              list="rename-target-options"
              value={to_name}
              onInput={(e: any) => setToName(e.currentTarget.value)}
              placeholder="e.g. Spoilage"
            />
            <datalist id="rename-target-options">
              {category_names
                .filter((n) => n.toLowerCase() !== (from_name ?? "").toLowerCase())
                .map((n) => (
                  <option key={n} value={n} />
                ))}
            </datalist>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!to_name.trim()}>
              Preview changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
